import React from "react";

// ---------------------------------------------------------------------
// STATS SHEETS
//
// Displays the filtered Incident Type x Period statistical matrix.
//
// Used ONLY by the general Statistics "View Spreadsheet" button.
//
// Data source:
//   GET /stats/agencies/:aid/statistical_matrix
//
// This component is intentionally separate from StatsReport.js, which
// remains responsible for the existing Working Spreadsheet.
// ---------------------------------------------------------------------
const StatsSheets = ({ statisticalMatrixData }) => {
    if (!statisticalMatrixData) {
        return null;
    }

    const columns = Array.isArray(
        statisticalMatrixData?.columns
    )
        ? statisticalMatrixData.columns
        : [];

    const rows = Array.isArray(
        statisticalMatrixData?.rows
    )
        ? statisticalMatrixData.rows
        : [];

    // Keep the input families visually and mathematically separate.
    //
    // Public = incident type selected by the public report schema.
    // Responder = incident type selected by the responder report schema.
    const publicRows = rows.filter(
        (row) => row?.incident_family === "public"
    );

    const responderRows = rows.filter(
        (row) => row?.incident_family === "responder"
    );

    // Preserve any future/unknown family rather than silently dropping it.
    const otherRows = rows.filter(
        (row) =>
            row?.incident_family !== "public" &&
            row?.incident_family !== "responder"
    );

    // -----------------------------------------------------------------
    // FAMILY TOTALS
    //
    // Public and Responder are separate statistical populations.
    // Never combine them into one grand total in this report.
    // -----------------------------------------------------------------
    const calculateFamilyTotals = (familyRows) => {
        const columnTotals = {};

        columns.forEach((column) => {
            columnTotals[column.key] = familyRows.reduce(
                (sum, row) =>
                    sum +
                    (Number(
                        row?.values?.[column.key]
                    ) || 0),
                0
            );
        });

        const total = familyRows.reduce(
            (sum, row) =>
                sum + (Number(row?.total) || 0),
            0
        );

        return {
            columnTotals,
            total,
        };
    };

    const publicTotals =
        calculateFamilyTotals(publicRows);

    const responderTotals =
        calculateFamilyTotals(responderRows);

    const otherTotals =
        calculateFamilyTotals(otherRows);

    const renderIncidentRow = (row) => (
        <tr
            key={`${row?.incident_family || "unknown"}-${row?.incident_type_id}`}
        >
            <td
                style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    background: "#fff",
                    minWidth: "230px",
                    maxWidth: "300px",
                    padding: "8px 10px",
                    border: "1px solid #d9d9d9",
                    fontWeight: 500,
                    textAlign: "left",
                    whiteSpace: "normal",
                }}
            >
                {row?.incident_type ||
                    "Untitled Incident Type"}
            </td>

            {columns.map((column) => (
                <td
                    key={`${row?.incident_family}-${row?.incident_type_id}-${column.key}`}
                    style={{
                        minWidth: "70px",
                        padding: "8px 10px",
                        border: "1px solid #d9d9d9",
                        textAlign: "center",
                        verticalAlign: "middle",
                    }}
                >
                    {Number(
                        row?.values?.[column.key]
                    ) || 0}
                </td>
            ))}

            <td
                style={{
                    minWidth: "80px",
                    padding: "8px 10px",
                    border: "1px solid #d9d9d9",
                    textAlign: "center",
                    verticalAlign: "middle",
                    fontWeight: 700,
                }}
            >
                {Number(row?.total) || 0}
            </td>
        </tr>
    );

    const renderFamilyHeading = (label, key) => (
        <tr key={key}>
            <td
                colSpan={columns.length + 2}
                style={{
                    padding: "8px 10px",
                    border: "1px solid #d9d9d9",
                    background: "#f3f5f7",
                    color: "#24364b",
                    fontWeight: 700,
                    textAlign: "left",
                }}
            >
                {label}
            </td>
        </tr>
    );

    const renderFamilyTotal = (
        label,
        totals,
        key
    ) => (
        <tr
            key={key}
            style={{
                fontWeight: 700,
                background: "#e6e6e6",
            }}
        >
            <td
                style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    padding: "9px 10px",
                    border: "1px solid #c9c9c9",
                    background: "#e6e6e6",
                    textAlign: "left",
                }}
            >
                {label}
            </td>

            {columns.map((column) => (
                <td
                    key={`${key}-${column.key}`}
                    style={{
                        padding: "9px 10px",
                        border: "1px solid #c9c9c9",
                        textAlign: "center",
                    }}
                >
                    {Number(
                        totals?.columnTotals?.[
                            column.key
                        ]
                    ) || 0}
                </td>
            ))}

            <td
                style={{
                    padding: "9px 10px",
                    border: "1px solid #c9c9c9",
                    textAlign: "center",
                }}
            >
                {Number(totals?.total) || 0}
            </td>
        </tr>
    );

    const renderSectionBreak = (key) => (
        <tr key={key}>
            <td
                colSpan={columns.length + 2}
                style={{
                    height: "24px",
                    padding: 0,
                    border: "none",
                    background: "#fff",
                }}
            />
        </tr>
    );

    return (
        <div
            style={{
                marginTop: "20px",
                background: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #ddd",
                    background: "#fafafa",
                }}
            >
                <div
                    style={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "#24364b",
                    }}
                >
                    Incident Statistics
                </div>

                <div
                    style={{
                        marginTop: "3px",
                        color: "#66717d",
                        fontSize: "0.85rem",
                    }}
                >
                    Incident types by selected reporting period
                </div>
            </div>

            <div
                style={{
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <table
                    style={{
                        width: "100%",
                        minWidth:
                            columns.length > 6
                                ? "1200px"
                                : "800px",
                        borderCollapse: "collapse",
                        fontSize: "0.82rem",
                    }}
                >
                    <thead>
                        <tr>
                            <th
                                style={{
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 3,
                                    minWidth: "230px",
                                    padding: "9px 10px",
                                    border: "1px solid #d9d9d9",
                                    background: "#e9ecef",
                                    color: "#24364b",
                                    textAlign: "left",
                                    verticalAlign: "middle",
                                }}
                            >
                                Incident Type
                            </th>

                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    style={{
                                        minWidth: "70px",
                                        padding: "9px 10px",
                                        border: "1px solid #d9d9d9",
                                        background: "#e9ecef",
                                        color: "#24364b",
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {column.label}
                                </th>
                            ))}

                            <th
                                style={{
                                    minWidth: "80px",
                                    padding: "9px 10px",
                                    border: "1px solid #d9d9d9",
                                    background: "#e9ecef",
                                    color: "#24364b",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                }}
                            >
                                Total
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {publicRows.length > 0 && (
                            <>
                                {renderFamilyHeading(
                                    "Public Incident Types",
                                    "public-heading"
                                )}

                                {publicRows.map(
                                    renderIncidentRow
                                )}

                                {renderFamilyTotal(
                                    "Public Total",
                                    publicTotals,
                                    "public-total"
                                )}
                            </>
                        )}

                        {publicRows.length > 0 &&
                            responderRows.length > 0 &&
                            renderSectionBreak(
                                "public-responder-break"
                            )}

                        {responderRows.length > 0 && (
                            <>
                                {renderFamilyHeading(
                                    "Responder Incident Types",
                                    "responder-heading"
                                )}

                                {responderRows.map(
                                    renderIncidentRow
                                )}

                                {renderFamilyTotal(
                                    "Responder Total",
                                    responderTotals,
                                    "responder-total"
                                )}
                            </>
                        )}

                        {(publicRows.length > 0 ||
                            responderRows.length > 0) &&
                            otherRows.length > 0 &&
                            renderSectionBreak(
                                "other-break"
                            )}

                        {otherRows.length > 0 && (
                            <>
                                {renderFamilyHeading(
                                    "Other Incident Types",
                                    "other-heading"
                                )}

                                {otherRows.map(
                                    renderIncidentRow
                                )}

                                {renderFamilyTotal(
                                    "Other Total",
                                    otherTotals,
                                    "other-total"
                                )}
                            </>
                        )}

                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={
                                        columns.length + 2
                                    }
                                    style={{
                                        padding: "24px",
                                        border: "1px solid #d9d9d9",
                                        textAlign: "center",
                                        color: "#66717d",
                                    }}
                                >
                                    No incidents match the selected filters.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StatsSheets;
